/**
 * The places the licence panel can send someone, as a closed set.
 *
 * A plugin UI cannot open a URL itself: `window.open` returns null inside the
 * iframe Figma sandboxes without `allow-popups`. Only the sandbox half can,
 * through `figma.openExternal` — so a click on "Buy Pro" becomes a message
 * asking it to. That makes the sandbox the thing deciding what gets opened,
 * and it should not take a URL on trust: whatever posts that message is, from
 * its point of view, a frame claiming to be our UI.
 *
 * So the message carries a NAME, not a URL. The sandbox looks the name up
 * here, and there is no string a caller can supply that opens anything off
 * this list. Both halves import the same table, so they cannot disagree about
 * what a name means. (This is `photoshop-to-figma/src/shared/links.ts`'s
 * reasoning, which was right, generalised to the family.)
 */

export const LICENSE_LINKS = {
  /** The one Lemon Squeezy product; the seat count is picked on the checkout page. */
  checkout: 'https://tomake.lemonsqueezy.com/checkout/buy/07091da1-6e2c-46b8-8edd-7c0980cac646',
  /**
   * Not a licence link, and here anyway.
   *
   * The footer's help menu offers "Send feedback", which is the same problem this table exists
   * to solve — a UI that cannot open a URL, asking the sandbox to open one, and a sandbox that
   * should not take a URL on trust. Giving feedback its own parallel table would be a second
   * copy of this reasoning for one entry. If a third kind of link appears, this is the moment
   * to rename the table rather than keep stretching the word "licence".
   *
   * pdf-to-figma calls `window.open` for this today, which the comment above says returns null
   * in the sandboxed iframe — so that menu row most likely does nothing there.
   */
  feedback: 'https://go.tomake.design/feedback',
} as const;

export type LicenseLinkName = keyof typeof LICENSE_LINKS;

/**
 * The URL for a name, or null when it is not one of ours.
 *
 * `hasOwnProperty`, not `in`: `in` walks the prototype chain, so `'toString' in
 * LICENSE_LINKS` is true and the lookup would hand back a function for the
 * sandbox to pass to `figma.openExternal`. Called off `Object.prototype` so the
 * table does not have to be an object that still has one.
 */
export function licenseLinkUrl(name: unknown): string | null {
  if (typeof name !== 'string') return null;
  return Object.prototype.hasOwnProperty.call(LICENSE_LINKS, name)
    ? LICENSE_LINKS[name as LicenseLinkName]
    : null;
}
